import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import type { EntryComponent, IRenderContext } from 'cms/entries/entries';
import { Desc } from 'components/landing-pages-v2/shared/Typography';
import { Picture } from 'components/shared/performance/pictures';
import type { ILpComponentMapBackgroundHero } from 'declarations/contentful';

import Grid from './Grid';
import Marker, { MarkerPulseClassName } from './shared/Marker';
import { resizeMapBackgroundImageUrl, resizeMapMarkerImageUrl } from './shared/utils';

const MAP_BACKGROUND_IMAGE_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-07-1689881024134-43912.jpg?preset=square-2048';
const MAP_MARKER_IMAGE_URLS = {
  leftTop:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416264-57298.png?preset=square-2048',
  rightTop:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416269-85419.png?preset=square-2048',
  leftMiddle:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416260-76025.png?preset=square-2048',
  leftBottom:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416240-88834.png?preset=square-2048',
  rightBottom:
    'https://images.opendoor.com/source/s3/imgdrop-production/2023-04-1682635416253-95450.png?preset=square-2048',
} as const;

const MAP_BACKGROUND_IMAGE_SOURCE = resizeMapBackgroundImageUrl(MAP_BACKGROUND_IMAGE_URL);
const MAP_BACKGROUND_IMAGE_SOURCES = [
  {
    key: MAP_BACKGROUND_IMAGE_SOURCE,
    media: '(min-width: 0px)',
    srcSet: MAP_BACKGROUND_IMAGE_SOURCE,
    fallbackSrc: MAP_BACKGROUND_IMAGE_SOURCE,
  },
];

const RenderMapBackgroundHero = (
  entry: ILpComponentMapBackgroundHero,
  _resolvedData?: unknown,
  context?: IRenderContext,
) => {
  const { fields } = entry;
  const isFirstBlockEntry = Boolean(context?.isFirstBlockEntry);

  return (
    <Col className="w-full pt-25">
      <Col
        analyticsName={`cosmos-landing-page-map-background-hero-${fields?.analyticsId}`}
        trackImpression
        className="relative min-h-163 w-full items-center justify-center py-5 md:min-h-137 md:py-8 lg:min-h-212"
      >
        <Col className="absolute left-0 right-0 top-0 z-0 h-full w-full">
          <Picture
            alt=""
            fallbackSrc={MAP_BACKGROUND_IMAGE_SOURCE}
            fetchPriority={isFirstBlockEntry ? 'high' : 'auto'}
            lazy={!isFirstBlockEntry}
            objectFit="cover"
            preload={isFirstBlockEntry}
            sources={MAP_BACKGROUND_IMAGE_SOURCES}
          />
        </Col>
        <Marker
          imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftTop)}
          left="10%"
          top="17%"
          height="$40x"
          width="$101x"
          $largerThanXS={{ top: '12%' }}
          $largerThanSM={{ top: '12%', height: '$51x', width: '$129x' }}
          className={MarkerPulseClassName}
          animationDelay="2s"
        />
        <Marker
          imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.rightTop)}
          right="7%"
          top="4%"
          height="$40x"
          width="$101x"
          $largerThanSM={{ right: '30%', top: '7%', height: '$51x', width: '$129x' }}
          className={MarkerPulseClassName}
          animationDelay="0.5s"
        />
        <Marker
          imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftMiddle)}
          display="none"
          left="8%"
          top="40%"
          height="$40x"
          width="$101x"
          $largerThanSM={{ display: 'flex', height: '$51x', width: '$129x' }}
          className={MarkerPulseClassName}
          animationDelay="0.5s"
        />
        <Marker
          imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.leftBottom)}
          left="15%"
          top="80%"
          height="$40x"
          width="$101x"
          $largerThanSM={{ top: '85%', height: '$51x', width: '$129x' }}
          className={MarkerPulseClassName}
        />
        <Marker
          imageSource={resizeMapMarkerImageUrl(MAP_MARKER_IMAGE_URLS.rightBottom)}
          right="4%"
          top="90%"
          height="$40x"
          width="$101x"
          $largerThanXS={{ top: '85%' }}
          $largerThanSM={{ right: '15%', top: '60%', height: '$51x', width: '$129x' }}
          className={MarkerPulseClassName}
          animationDelay="2.5s"
        />
        <Grid justifyContent="center" position="relative">
          <Col className="col-span-12 z-1 items-center justify-center gap-12">
            <Col className="w-full items-center gap-6">
              <Col className="items-center">
                <Text
                  as="h2"
                  className="block text-center text-header-sm text-od-content-inverse-primary md:text-header-md lg:text-header-lg"
                >
                  {fields?.title}
                </Text>
                <Text
                  as="h2"
                  className="block text-center text-header-sm text-od-content-inverse-secondary opacity-65 md:text-header-md lg:text-header-lg"
                >
                  {fields?.secondaryTitle}
                </Text>
              </Col>
              <Row className="w-full max-w-68 justify-center lg:max-w-94">
                <Desc
                  textAlign="center"
                  typography="$bodyMedium"
                  $largerThanMD={{ typography: '$bodyXlarge' }}
                  color={'$contentInversePrimary'}
                >
                  {fields?.description}
                </Desc>
              </Row>
            </Col>
            {fields?.ctaLink && fields?.ctaDisplayText && (
              <Button
                variant="primary"
                aria-label={fields?.ctaDisplayText}
                analyticsName={`cosmos-landing-page-map-background-hero-cta-${fields?.analyticsId}`}
                href={fields?.ctaLink}
                size="sm"
                className="h-15 max-w-41 px-6"
              >
                {fields?.ctaDisplayText}
              </Button>
            )}
          </Col>
        </Grid>
      </Col>
    </Col>
  );
};

const MapBackgroundHero: EntryComponent<ILpComponentMapBackgroundHero> = {
  render: RenderMapBackgroundHero,
};

export default MapBackgroundHero;
